import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiUserCheck, FiTrendingDown, FiTool, FiArrowRight, FiInfo, FiLayers } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const SelfBuild = () => {
  const supportPoints = [
    "Planung einzelner Arbeitsschritte",
    "Fachgerechte Ausführung",
    "Auswahl geeigneter Baustoffe",
    "Suche nach preiswerten und zuverlässigen Materiallieferanten",
    "Fragen rund um Mauer-, Beton- und Sanierungsarbeiten",
    "Vermeidung teurer Fehler"
  ];

  const advantages = [
    { title: "Kostenersparnis", desc: "Deutlich geringere Baukosten durch Eigenleistung.", icon: FiTrendingDown },
    { title: "Fachliche Sicherheit", desc: "Fachliche Sicherheit durch unseren Meisterbetrieb.", icon: FiUserCheck },
    { title: "Praxis-Tipps", desc: "Praktische Tipps aus der täglichen Baustellen praxis.", icon: FiTool },
    { title: "Material-Service", desc: "Profitieren Sie von unseren Kontakten zu zuverlässigen Materiallieferanten.", icon: FiLayers },
    { title: "Flexibilität", desc: "Unterstützung genau dann, wenn Sie sie benötigen.", icon: FiCheckCircle },
    { title: "Qualitätssicherung", desc: "Mehr Qualität und weniger kostspielige Fehler.", icon: FiInfo }
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-slate-900 py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px]"></div>
        </div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-8 uppercase tracking-tight">
              Bauen in <span className="text-red-500">Eigenleistung</span>
            </h1>
            <motion.div 
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-24 h-2 bg-red-800 rounded-full mx-auto mb-8"
            ></motion.div>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed"
            >
              Bauen muss nicht unnötig teuer sein. Packen Sie selbst mit an – mit unserer professionellen Begleitung.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Concept Section - Jetzt ohne Bild und zentriert */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 border-l-8 border-red-800 pl-6">
                Unser Konzept
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Viele Bauherren möchten selbst mit anpacken, wissen aber oft nicht genau, wie bestimmte Arbeiten richtig ausgeführt werden oder wo sie hochwertige Materialien zu fairen Preisen bekommen. Genau dabei unterstützen wir Sie.
              </p>
              <p className="text-xl font-bold text-slate-800 mb-12 italic bg-slate-50 p-8 rounded-3xl border-l-4 border-red-800">
                "Sie übernehmen die Arbeiten in Eigenleistung – wir stehen Ihnen als erfahrener Meisterbetrieb mit Rat und Tat zur Seite."
              </p>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-8">Wir unterstützen Sie unter anderem bei:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {supportPoints.map((point, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 text-slate-700 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm"
                  >
                    <div className="w-8 h-8 bg-red-800 text-white rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCheckCircle} className="text-sm" />
                    </div>
                    <span className="text-lg font-medium">{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advantages Grid */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4">Ihre Vorteile</h2>
            <p className="text-slate-600 text-lg">Warum sich eine professionelle Begleitung auszahlt.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {advantages.map((adv, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-10 rounded-[2rem] shadow-sm border border-slate-100 hover:shadow-xl transition-all group"
              >
                <div className="w-16 h-16 bg-red-50 text-red-800 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-red-800 group-hover:text-white transition-colors">
                  <SafeIcon icon={adv.icon} className="text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{adv.title}</h3>
                <p className="text-slate-600 leading-relaxed">{adv.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Goal & CTA */}
      <section className="py-24 px-6">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-red-800 rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden shadow-2xl"
          >
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:20px_20px]"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">Gemeinsam zum Eigenheim</h2>
              <p className="text-red-100 text-xl mb-12 max-w-3xl mx-auto">
                Unser Ziel ist es, Ihnen dabei zu helfen, Ihr Bauprojekt erfolgreich, sicher und möglichst kostengünstig umzusetzen. Haben Sie Fragen oder möchten Sie Ihr Bauvorhaben besprechen? Dann kontaktieren Sie uns – wir freuen uns darauf, Sie auf Ihrem Weg zum Eigenheim zu begleiten.
              </p>
              <Link 
                to="/kontakt" 
                className="inline-flex items-center gap-3 bg-white text-red-800 px-10 py-5 rounded-2xl font-bold text-xl hover:bg-slate-100 transition-all shadow-xl hover:-translate-y-1"
              >
                Jetzt Kontakt aufnehmen
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SelfBuild;